import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { post } from '../../utils/api';
import { CONTACT_SUBJECT_OPTIONS } from '../../constants/contactSubjects';
import { CloseX, ignoreBackdropEscapeClose } from './portalUi';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = !!(RECAPTCHA_SITE_KEY && RECAPTCHA_SITE_KEY.trim());
const AUTO_CLOSE_SEC = 30;

function inferSource(pathname) {
  const p = String(pathname || '');
  if (p.startsWith('/donate') || p.startsWith('/product/')) return 'public_donate';
  if (p.startsWith('/devotees/')) return 'portal';
  if (p.startsWith('/login')) return 'login';
  return 'other';
}

/**
 * @param {{ open: boolean, onClose: () => void }} props
 */
export default function ContactSupportDialog({ open, onClose }) {
  const location = useLocation();
  const { staff, loading: authLoading } = useAuth();
  const loggedIn = !!staff;

  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [comment, setComment] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [autoCloseSec, setAutoCloseSec] = React.useState(0);
  const [msg, setMsg] = React.useState({ type: '', text: '' });
  const [recaptchaToken, setRecaptchaToken] = React.useState(null);
  const recaptchaRef = React.useRef(null);
  const autoCloseTimerRef = React.useRef(null);
  const countdownRef = React.useRef(null);

  const clearAutoCloseTimers = React.useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleClose = React.useCallback(() => {
    clearAutoCloseTimers();
    onClose();
  }, [clearAutoCloseTimers, onClose]);

  React.useEffect(() => {
    if (!open) {
      clearAutoCloseTimers();
      return;
    }
    setSubmitted(false);
    setAutoCloseSec(0);
    setMsg({ type: '', text: '' });
    setComment('');
    setSubject('');
    setPhone('');
    setRecaptchaToken(null);
    recaptchaRef.current?.reset();
    if (loggedIn) {
      setName(String(staff?.name || '').trim());
      setEmail(String(staff?.email || '').trim());
    } else {
      setName('');
      setEmail('');
    }
  }, [open, loggedIn, staff?.name, staff?.email, clearAutoCloseTimers]);

  React.useEffect(() => {
    if (!open || !submitted) return undefined;
    setAutoCloseSec(AUTO_CLOSE_SEC);
    countdownRef.current = setInterval(() => {
      setAutoCloseSec((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    autoCloseTimerRef.current = setTimeout(() => {
      handleClose();
    }, AUTO_CLOSE_SEC * 1000);
    return () => clearAutoCloseTimers();
  }, [open, submitted, handleClose, clearAutoCloseTimers]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitted) return;
    setMsg({ type: '', text: '' });
    if (!subject) {
      setMsg({ type: 'error', text: 'Please choose a subject.' });
      return;
    }
    if (!phone.trim()) {
      setMsg({ type: 'error', text: 'Phone number is required.' });
      return;
    }
    if (!loggedIn && RECAPTCHA_ENABLED && !recaptchaToken) {
      setMsg({ type: 'error', text: 'Please complete the reCAPTCHA check.' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        subject,
        comment: comment.trim(),
        source: inferSource(location.pathname),
        pagePath: location.pathname,
      };
      if (!loggedIn && RECAPTCHA_ENABLED) {
        payload.recaptchaToken = recaptchaToken;
      }
      await post('/public/contact', payload);
      setSubmitted(true);
      setMsg({
        type: 'success',
        text: 'Thank you. We received your message and sent a confirmation to your email.',
      });
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || 'Could not send your message. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(ev, reason) => {
        if (ignoreBackdropEscapeClose(ev, reason)) return;
        if (!submitting) handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Typography variant="h6" component="span">
          Contact us
        </Typography>
        <CloseX onClick={handleClose} disabled={submitting} />
      </DialogTitle>
      <DialogContent dividers>
        <Box component="form" onSubmit={onSubmit}>
          {!submitted ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Questions about your account or donations? Our team will follow up by email.
            </Typography>
          ) : null}
          {loggedIn && !submitted ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Signed in — your account name and email are used for this message.
            </Typography>
          ) : null}
          <TextField
            label="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            size="small"
            disabled={submitting || authLoading || loggedIn}
            slotProps={{ input: { readOnly: submitted } }}
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            size="small"
            disabled={submitting || authLoading || loggedIn}
            slotProps={{ input: { readOnly: submitted } }}
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            required
            size="small"
            disabled={submitting}
            slotProps={{ input: { readOnly: submitted } }}
            sx={{ mb: 1.5 }}
          />
          <FormControl fullWidth required size="small" sx={{ mb: 1.5 }} disabled={submitting || submitted}>
            <InputLabel id="contact-subject-label">Subject</InputLabel>
            <Select
              labelId="contact-subject-label"
              id="contact-subject"
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            >
              <MenuItem value="" disabled>
                Choose
              </MenuItem>
              {CONTACT_SUBJECT_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Comments"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            fullWidth
            required
            multiline
            minRows={4}
            size="small"
            disabled={submitting}
            slotProps={{ input: { readOnly: submitted } }}
            sx={{ mb: 1.5 }}
          />
          {!loggedIn && RECAPTCHA_ENABLED && !submitted ? (
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => setRecaptchaToken(token || null)}
                onExpired={() => setRecaptchaToken(null)}
              />
            </Box>
          ) : null}
          {msg.text ? (
            <Alert severity={msg.type === 'error' ? 'error' : 'success'} sx={{ mb: 1.5 }}>
              {msg.text}
              {submitted && autoCloseSec > 0 ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  This window will close in {autoCloseSec} second{autoCloseSec === 1 ? '' : 's'}, or use Close when you are
                  done.
                </Typography>
              ) : null}
            </Alert>
          ) : null}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="outlined" onClick={handleClose} disabled={submitting}>
              Close
            </Button>
            {!submitted ? (
              <Button type="submit" variant="contained" disabled={submitting || authLoading}>
                {submitting ? 'Sending…' : 'Send message'}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
